// Voice filters: plain DSP in Web Audio, no AI. One chain per character; preview and export build the
// same chain (export inside an OfflineAudioContext), so what you hear is what the MP4 gets.
//
// input -> pitch shifter (AudioWorklet) -> size/tone EQ -> dry + one effect branch -> output
//
// Every chain delays the sound by exactly VOICE_LATENCY (the pitch shifter's half window, also when the
// pitch is 0), so playback and export shift their clocks by that amount and mouths stay on the words.
// Nothing here changes a line's speed or length.
import type { VoiceEffect, VoiceSettings } from "../../model/voice";
import { PITCH_WORKLET, PITCH_WINDOW } from "./pitchWorklet";

export const VOICE_LATENCY = PITCH_WINDOW / 2;

export const NATURAL: VoiceSettings = { pitch: 0, size: 0, tone: 0, effect: "none", amount: 0.7 };

export const VOICE_PRESETS: { id: string; name: string; voice: VoiceSettings }[] = [
  { id: "natural", name: "Natural", voice: NATURAL },
  { id: "small", name: "Small", voice: { pitch: 3, size: -0.8, tone: 0.3, effect: "none", amount: 0.7 } },
  { id: "big", name: "Big", voice: { pitch: -3, size: 0.8, tone: -0.3, effect: "none", amount: 0.7 } },
  { id: "robot", name: "Robot", voice: { pitch: -1, size: 0, tone: 0.2, effect: "robot", amount: 0.85 } },
  { id: "radio", name: "Radio", voice: { pitch: 0, size: 0, tone: 0.2, effect: "radio", amount: 0.9 } },
  { id: "gravel", name: "Gravel", voice: { pitch: -2, size: 0.3, tone: -0.2, effect: "gravel", amount: 0.7 } },
  { id: "wobbly", name: "Wobbly", voice: { pitch: 1, size: -0.2, tone: 0, effect: "wobble", amount: 0.75 } },
];

export const VOICE_EFFECTS: { id: VoiceEffect; name: string }[] = [
  { id: "none", name: "None" }, { id: "robot", name: "Robot" }, { id: "radio", name: "Radio" },
  { id: "gravel", name: "Gravel" }, { id: "wobble", name: "Wobble" }, { id: "echo", name: "Echo" },
];

const same = (a: VoiceSettings, b: VoiceSettings) =>
  Math.abs(a.pitch - b.pitch) < 0.01 && Math.abs(a.size - b.size) < 0.01 && Math.abs(a.tone - b.tone) < 0.01 &&
  a.effect === b.effect && (a.effect === "none" || Math.abs(a.amount - b.amount) < 0.01);
export const presetOf = (v: VoiceSettings) => VOICE_PRESETS.find(p => same(p.voice, v))?.id ?? "custom";

// Fills in anything missing (old saves, partial JSON) and clamps to the slider ranges.
export function normaliseVoice(v: Partial<VoiceSettings> | undefined): VoiceSettings {
  const n = (x: unknown, lo: number, hi: number, d: number) => (typeof x === "number" && isFinite(x) ? Math.max(lo, Math.min(hi, x)) : d);
  return {
    pitch: n(v?.pitch, -12, 12, 0),
    size: n(v?.size, -1, 1, 0),
    tone: n(v?.tone, -1, 1, 0),
    effect: VOICE_EFFECTS.some(e => e.id === v?.effect) ? v!.effect! : "none",
    amount: n(v?.amount, 0, 1, 0.7),
  };
}

// The worklet module has to be added to each context once before any chain is built in it.
const loaded = new WeakMap<BaseAudioContext, Promise<void>>();
let moduleUrl: string | null = null;
export function prepareVoice(ctx: BaseAudioContext): Promise<void> {
  let p = loaded.get(ctx);
  if (!p) {
    moduleUrl ??= URL.createObjectURL(new Blob([PITCH_WORKLET], { type: "text/javascript" }));
    p = ctx.audioWorklet.addModule(moduleUrl);
    loaded.set(ctx, p);
  }
  return p;
}

export type VoiceChain = { update(v: VoiceSettings): void; dispose(): void };

function curve(fn: (x: number) => number, n = 2048) {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) c[i] = fn((i / (n - 1)) * 2 - 1);
  return c;
}
// Radio: soft clip, then 5-bit-ish amplitude steps (bit crush).
const RADIO_CURVE = curve(x => Math.round((Math.tanh(3 * x) / Math.tanh(3)) * 14) / 14, 4096);
// Safety: linear up to 0.7, then a soft knee that never quite reaches full scale.
const LIMIT_CURVE = curve(x => {
  const a = Math.abs(x);
  return Math.sign(x) * (a < 0.7 ? a : 0.7 + 0.28 * Math.tanh((a - 0.7) / 0.28));
});
// Gravel: asymmetric tanh, so it buzzes rather than fizzes.
const GRAVEL_CURVE = curve(x => Math.tanh(5 * x + 0.25) - Math.tanh(0.25));

// Builds the chain between `input` and `output`. Call prepareVoice(ctx) first.
// update() moves every parameter smoothly, so sliders can be dragged while a line plays.
export function buildVoiceChain(ctx: BaseAudioContext, voice: VoiceSettings, input: AudioNode, output: AudioNode): VoiceChain {
  const nodes: AudioNode[] = [];
  const oscs: OscillatorNode[] = [];
  const add = <T extends AudioNode>(n: T) => (nodes.push(n), n);
  const gain = (v = 1) => { const g = add(ctx.createGain()); g.gain.value = v; return g; };
  const biquad = (type: BiquadFilterType, f: number, q = 0.707) => { const b = add(ctx.createBiquadFilter()); b.type = type; b.frequency.value = f; b.Q.value = q; return b; };
  const osc = (f: number) => { const o = add(ctx.createOscillator()); o.frequency.value = f; o.start(); oscs.push(o); return o; };
  const chain = (...ns: AudioNode[]) => { for (let i = 0; i < ns.length - 1; i++) ns[i]!.connect(ns[i + 1]!); };

  const pitch = add(new AudioWorkletNode(ctx, "voice-pitch", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1], channelCount: 1, channelCountMode: "explicit" }));
  const semis = pitch.parameters.get("semitones")!;
  const thin = biquad("highpass", 60);
  const low = biquad("lowshelf", 260);
  const high = biquad("highshelf", 3200);
  const presence = biquad("peaking", 1400, 0.9);
  chain(input, pitch, thin, low, presence, high);
  const eq = high;

  // Everything meets in `mix`, gets a loudness trim and a soft safety clip, then goes out.
  const mix = gain(1), trim = gain(1);
  const limit = add(ctx.createWaveShaper()); limit.curve = LIMIT_CURVE;
  chain(mix, trim, limit, output);
  output = mix;

  const dry = gain(1);
  chain(eq, dry, output);

  // Robot: ring modulator into a short metallic comb.
  const ring = gain(0);
  osc(48).connect(ring.gain);
  const comb = add(ctx.createDelay(0.05)); comb.delayTime.value = 0.0085;
  const combFb = gain(0.5);
  const robotOut = gain(0);
  chain(eq, ring, comb, robotOut, output);
  chain(comb, combFb, comb);
  ring.connect(robotOut);

  // Radio: telephone band, soft clip and bit crush.
  const radioHp = biquad("highpass", 450, 0.9), radioLp = biquad("lowpass", 3000, 0.9);
  const radioShape = add(ctx.createWaveShaper()); radioShape.curve = RADIO_CURVE;
  const radioPre = gain(1.6), radioOut = gain(0);
  chain(eq, radioHp, radioLp, radioPre, radioShape, radioOut, output);

  // Gravel: light distortion, tamed above 5 kHz.
  const gravelPre = gain(1.4);
  const gravelShape = add(ctx.createWaveShaper()); gravelShape.curve = GRAVEL_CURVE; gravelShape.oversample = "2x";
  const gravelLp = biquad("lowpass", 5000), gravelOut = gain(0);
  chain(eq, gravelPre, gravelShape, gravelLp, gravelOut, output);

  // Wobble: vibrato from a short delay swept by a slow oscillator.
  const vib = add(ctx.createDelay(0.05)); vib.delayTime.value = 0.006;
  const lfoDepth = gain(0);
  osc(5.5).connect(lfoDepth);
  lfoDepth.connect(vib.delayTime);
  const wobbleOut = gain(0);
  chain(eq, vib, wobbleOut, output);

  // Echo: feedback delay, darker on each repeat, mixed low.
  const echo = add(ctx.createDelay(1)); echo.delayTime.value = 0.17;
  const echoLp = biquad("lowpass", 2600), echoFb = gain(0.35), echoOut = gain(0);
  chain(eq, echo, echoLp, echoOut, output);
  chain(echoLp, echoFb, echo);

  let first = true;
  const set = (p: AudioParam, v: number) => {
    if (first) p.value = v;
    else p.setTargetAtTime(v, ctx.currentTime, 0.02);
  };

  function update(raw: VoiceSettings) {
    const v = normaliseVoice(raw);
    const big = Math.max(0, v.size), small = Math.max(0, -v.size);
    // Size moves pitch and formants together (the shifter resamples grains) and adds a matching tilt.
    set(semis, Math.max(-24, Math.min(24, v.pitch - v.size * 4)));
    set(thin.frequency, 60 + small * 260);
    set(low.gain, big * 6 - small * 3 - v.tone * 4);
    set(presence.gain, small * 3 - big * 2);
    set(high.gain, -big * 4 + small * 2 + v.tone * 7);
    const a = v.effect === "none" ? 0 : v.amount;
    const wet = (e: VoiceEffect) => (v.effect === e ? a : 0);
    // Dry fades out as a replacing effect fades in; echo is added on top of the dry voice. Wobble is
    // always fully wet (mixing a vibrato with the dry voice only makes a chorus); amount sets its depth.
    // The gains below were matched by ear and by RMS so presets play at about the same loudness.
    set(dry.gain, v.effect === "echo" || v.effect === "none" ? 1 : v.effect === "wobble" ? 0 : 1 - a);
    set(robotOut.gain, wet("robot") * 1.25);
    set(radioOut.gain, wet("radio") * 0.55);
    set(gravelOut.gain, wet("gravel") * 0.5);
    set(wobbleOut.gain, v.effect === "wobble" ? 1 : 0);
    set(lfoDepth.gain, wet("wobble") * 0.0024);
    set(echoOut.gain, wet("echo") * 0.38);
    set(trim.gain, Math.pow(10, (-big * 3.5 + small * 1 - Math.max(0, -v.tone) * 1) / 20));
    first = false;
  }
  update(voice);

  return {
    update,
    dispose() {
      for (const o of oscs) try { o.stop(); } catch {}
      for (const n of nodes) try { n.disconnect(); } catch {}
    },
  };
}
